/* eslint-disable max-lines */
import { useCallback, useState, useEffect } from "react";
import {
  useDtfArtworks,
  useArtworkForm,
  useImagesWarmFastCache,
  useArtwork
} from "@design-lab/entities/artwork";
import type { Artwork } from "@design-lab/shared/types/artwork";
import { calculatedArtworkDimensions } from "@design-lab/entities/artwork/lib/calculateDimensions";
import type { PlacementFormPosition } from "@design-lab/shared/types";
import {
  trackDesignUploadCompleted,
  trackDesignUploadStarted
} from "@design-lab/shared/lib/analytics";
import { useAsyncEffect } from "@design-lab/shared/hooks/useAsyncEffect";
import { useCurrentComposition } from "@design-lab/entities/composition";
import { useProductColors } from "@design-lab/entities/product";
import { STANDARD_DTF_CATALOG_NUMBER } from "@design-lab/shared/constants/constants";

import { useDtfChannel } from "@design-lab/shared/hooks/useDtfChannel";
import { generateUUID } from "common/utils/uuid";
import { normalizeArtworkResponse } from "@design-lab/shared/lib/enhancementPolling";

type UseDesignLibrarySelectionParams = {
  placement: PlacementFormPosition;
  onSuccess?: () => void;
};

/**
 * Manages selection of an artwork from the design library for a given placement and updates the artwork form, cache, and analytics.
 *
 * When an artwork is selected, this hook retrieves artwork details, warms the image cache, updates the composition form values (size and artwork IDs) for the specified placement, sends machine state updates, and tracks upload start/completion. It also prevents race conditions by associating a generated upload UUID with each selection and invokes `onSuccess` after processing.
 *
 * @param placement - Which placement to apply the selected artwork to ("front" or "back")
 * @param onSuccess - Optional callback invoked after the selected artwork has been processed and form state updated
 * @returns An object with `selectArtwork`, a function that accepts an artwork ID (number) to begin the selection and processing flow
 */
export function useDesignLibrarySelection({
  placement,
  onSuccess
}: UseDesignLibrarySelectionParams) {
  const { getMachineBySide } = useArtwork();
  const { currentComposition: composition } = useCurrentComposition();
  const { warmCache } = useImagesWarmFastCache();
  const channel = useDtfChannel();
  const { form } = useArtworkForm();
  const { currentProduct } = useProductColors();

  const [selectedArtworkId, setSelectedArtworkId] = useState<string | null>(null);
  const [dtfChannelUuid, setDtfChannelUuid] = useState<string>("");
  // Use a Map to store uploadUuid per artworkId to prevent race conditions
  const [uploadUuids, setUploadUuids] = useState<Map<string, string>>(new Map());

  const side = placement === "back" ? "back" : "front";
  const machine = getMachineBySide(side);

  // Helper function to determine business line and order type
  const getBusinessLineAndOrderType = useCallback(() => {
    const catalogNumber = currentProduct?.catalogNumber || STANDARD_DTF_CATALOG_NUMBER;

    // Design lab is always used for print-on-demand services
    const businessLine = "Print";
    const orderType = "print";

    return { businessLine, orderType, catalogNumber };
  }, [currentProduct]);

  // Get DTF channel UUID
  useEffect(() => {
    const fetchChannelUuid = async () => {
      const uuid = await channel.retrieveDtfChannelUuid();
      setDtfChannelUuid(uuid || "");
    };

    fetchChannelUuid();
  }, [channel]);

  // Query for artwork details when an artwork is selected
  const queryParams = {
    apiType: "details",
    catalogNumber: STANDARD_DTF_CATALOG_NUMBER,
    ...(selectedArtworkId && {
      artworkIds: [selectedArtworkId],
      dtfChannelUuid
    })
  };

  const { data: artworkDetails } = useDtfArtworks(queryParams);

  // Handle artwork selection response
  useAsyncEffect(async () => {
    if (!artworkDetails || !selectedArtworkId) return;

    // Process the artwork data from the response
    const { artworks } = artworkDetails as any;
    if (!artworks || artworks.length === 0) return;

    const artwork = artworks[0];
    const artworkId = artwork.id.toString();

    // Get the uploadUuid for this specific artwork to prevent race conditions
    const uploadUuid = uploadUuids.get(artworkId);

    // Add artwork to the store with all enhancement images
    const polishedArtwork = {
      ...artwork.ordered,
      ...normalizeArtworkResponse(artwork)
    };

    const url = artwork.original?.imageUrl || artwork.ordered?.imageUrl || "";

    machine.send({
      type: "reset",
      targetState: "finished",
      context: {
        visitedStates: [],
        artworkId: polishedArtwork.id,
        artwork: polishedArtwork as unknown as Artwork,
        cacheWarmingStatus: "none"
      }
    });

    const { businessLine, orderType, catalogNumber } = getBusinessLineAndOrderType();
    const dpi = artwork.ordered?.dpi || artwork.original?.dpi || 300;

    trackDesignUploadCompleted(side, artworkId, "design_library", {
      catalogNumber,
      businessLine,
      orderType,
      page: "design_lab",
      pdpType: orderType,
      projectId: null,
      multiArtwork: false,
      artworksCount: 1,
      dpi,
      preCutTransfers: false,
      ...(uploadUuid && { uploadUuid })
    });

    const [cachedImage] = await warmCache([url]);
    form.setValue("currentPlacement", placement);

    const artworkDimensions = calculatedArtworkDimensions(
      cachedImage?.image,
      composition?.surfaces[placement]
    );
    if (placement === "back") {
      form.setValue(`${placement}.size`, {
        width: artworkDimensions.maxWidthInches,
        height: artworkDimensions.maxHeightInches
      });
      form.setValue(`back.artworkId`, artworkId);
      form.setValue(`info.back.enhancementTier`, "full_enhancements");
      form.setValue(`info.back.removeBackground`, true);
      form.setValue(`info.back.editImageManual`, polishedArtwork.editImageManual);
    } else {
      form.setValue(`front.size`, {
        width: artworkDimensions.maxWidthInches,
        height: artworkDimensions.maxHeightInches
      });
      form.setValue(`front.artworkId`, artworkId);
      form.setValue(`frontLeftChest.artworkId`, artworkId);
      form.setValue(`info.front.enhancementTier`, "full_enhancements");
      form.setValue(`info.front.removeBackground`, true);
      form.setValue(`info.front.editImageManual`, polishedArtwork.editImageManual);
    }

    // Clean up the uploadUuid for this artwork and reset selection
    setUploadUuids(prev => {
      const newMap = new Map(prev);
      newMap.delete(artworkId);
      return newMap;
    });
    setSelectedArtworkId(null);
    onSuccess?.();
  }, [
    artworkDetails,
    selectedArtworkId,
    placement,
    onSuccess,
    form,
    composition,
    warmCache,
    getBusinessLineAndOrderType,
    uploadUuids
  ]);

  const selectArtwork = useCallback(
    (artworkId: number, fileType?: string) => {
      if (!artworkId) return;

      const generatedUploadUuid = generateUUID();
      const { businessLine, orderType, catalogNumber } = getBusinessLineAndOrderType();
      trackDesignUploadStarted(side, "design_library", {
        catalogNumber,
        businessLine,
        orderType,
        page: "design_lab",
        pdpType: orderType,
        projectId: null,
        multiArtwork: false,
        artworksCount: 1,
        dpi: 300,
        preCutTransfers: false,
        uploadUuid: generatedUploadUuid,
        ...(fileType && { fileType })
      });

      const artworkIdString = artworkId.toString();
      setSelectedArtworkId(artworkIdString);
      setUploadUuids(prev => {
        const newMap = new Map(prev);
        newMap.set(artworkIdString, generatedUploadUuid);
        return newMap;
      });
    },
    [side, getBusinessLineAndOrderType]
  );

  return {
    selectArtwork
  };
}
